import { currencyFormatter } from '@dnhb/formatters';
import { useEffect, Fragment, useState } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { CurrencyField, PercentageField, SelectField, SplitDateField, CheckboxField } from 'shared/fields';
import { LoanpartButton, DeleteButton } from 'shared/buttons';
import { InputsContainer } from 'shared/containers';
import { Heading, StatelessCollapsable, LabelText } from 'shared/components';
import PlusSvg from 'images/hypotheekbespaarcheck/plus.svg';
import { useNavigate } from "react-router-dom";

export default function Loanparts({
  values,
  submitErrors,
  settings: {
    meta: { mortgage_types },
  },
  form: {
    mutators: { push },
  },
}) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/hypotheekbespaarcheck/loan-parts');
  }, [navigate]);

  const [activeLoanpart, setActiveLoanpart] = useState(0);

  return <>
    <Heading>Leningdelen</Heading>
    <InputsContainer>
      <FieldArray name="loanparts">
        {({ fields }) =>
          fields.map((loanpart, index) => {
            const loanpartData = fields.value[index];
            const { remaining_debt, mortgage_type, variable_interest_rate } = loanpartData;
            const mortgageType = (mortgage_types.find(type => type.value === mortgage_type) || {}).label;
            const remainingDebtSubTitle =
              remaining_debt || remaining_debt === 0 ? ' - ' + currencyFormatter(remaining_debt) : '';
            const subTitle = mortgageType
              ? `${mortgageType} ${remainingDebtSubTitle}`
              : 'Vul de leningdeelgegevens in';
            return (
              <Fragment key={loanpart}>
                <StatelessCollapsable
                  key={loanpart}
                  title={`Leningdeel ${index + 1}`}
                  subTitle={subTitle}
                  isOpen={activeLoanpart === index}
                  hasErrors={Boolean(Object.keys(submitErrors?.loanparts?.[index] ?? []).length)}
                  handleClick={() => setActiveLoanpart(activeLoanpart !== index ? index : null)}
                >
                  <LabelText css={{ marginTop: '8px!important' }}>Hypotheekvorm van dit leningdeel</LabelText>
                  <SelectField
                    id={`${loanpart}.mortgage_type`}
                    placeholder="Hypotheekvorm"
                    name={`${loanpart}.mortgage_type`}
                    options={mortgage_types || []}
                    emptyOption
                  />
                  <LabelText>Wat is je resterende schuld op dit leningdeel?</LabelText>
                  <CurrencyField placeholder="Bedrag" name={`${loanpart}.remaining_debt`} />
                  <LabelText>Rentepercentage van dit leningdeel</LabelText>
                  <PercentageField placeholder="Rente" name={`${loanpart}.interest_rate`} />
                  <LabelText>Einde huidige rentevaste periode</LabelText>
                  <SplitDateField name={`${loanpart}.frt_end_date`} disabled={variable_interest_rate} />
                  <CheckboxField
                    label="Dit leningdeel heeft een variabele rente"
                    id={`${loanpart}.variable_interest_rate`}
                    name={`${loanpart}.variable_interest_rate`}
                  />
                  <LabelText>Einddatum hypotheek van dit leningdeel</LabelText>
                  <SplitDateField name={`${loanpart}.end_date`} />
                  {fields.length > 1 && (
                    <DeleteButton
                      onClick={e => {
                        e.stopPropagation();
                        const result = window.confirm('Weet je zeker dat je dit leningdeel wil verwijderen?');
                        if (result) {
                          fields.remove(index);
                          if (
                            Array.isArray(submitErrors?.loanparts) &&
                            submitErrors.loanparts.length > index
                          ) {
                            submitErrors.loanparts.splice(index, 1);
                          }
                          setActiveLoanpart(null);
                        }
                      }}
                      type="button"
                    >
                      Leningdeel verwijderen
                    </DeleteButton>
                  )}
                </StatelessCollapsable>
              </Fragment>
            );
          })
        }
      </FieldArray>

      <LoanpartButton
        type="button"
        onClick={() => {
          push('loanparts', {variable_interest_rate: false});
          setActiveLoanpart(values.loanparts.length);
        }}
      >
        <PlusSvg/>
        Nog een leningdeel toevoegen
      </LoanpartButton>
    </InputsContainer>
  </>;
}
